import React from 'react'
import classNames from 'classnames'
import { Form } from 'react-bootstrap'
import { type User } from '../../../models/User'
import { useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { SelectDropdown, type SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { Spinner } from '../../../components/Spinner/Spinner'
import { type SendBookingInvitationsOwners } from './useSendBookingInvitationsOwners'
import { FormSection } from './FormSection'
import { SkippedClientsPopover, type SkippedClientsGroup } from './SkippedClientsPopover'
import { FallbackModes, MeetingOwnerModes, type FallbackMode, type MeetingOwnerMode } from './meetingOwnerMode'
import {
  MEETING_OWNER_DESCRIPTION,
  MEETING_OWNER_EACH,
  MEETING_OWNER_LOAD_ERROR,
  MEETING_OWNER_SELECT_USER,
  MEETING_OWNER_SINGLE,
  MEETING_OWNER_TITLE,
  MEETING_OWNER_USER,
  SKIPPED_GROUP_NO_OWNER,
  SKIPPED_GROUP_OWNER_NOT_SHARED,
  NO_OWNER_SELECT_LABEL,
  NO_OWNER_YOU,
  PICK_BACKUP_OWNER,
  UNKNOWN_USER,
  cantSendFromOwnerSentence,
  noOwnerSentence,
  noOwnerSkip,
  ownerNotSharedSentence,
  sendFromOption,
  skippedBreakdownSentence,
  sentFromInsteadSentence,
} from './sendBookingInvitationsCopy'

interface MeetingOwnerOptionProps {
  title: string
  selected: boolean
  onSelect: () => void
}

const MeetingOwnerOption: React.FC<MeetingOwnerOptionProps> = ({ title, selected, onSelect }) => (
  <label
    className={classNames('d-flex align-items-center gap-3 p-3 border rounded-3 cursor-pointer h-100', {
      'zocks-border-color-primary zocks-bg-color-light-blue': selected,
      'zocks-border-color-gray-300 zocks-bg-color-white': !selected,
    })}
  >
    <Form.Check.Input type='radio' name='meeting-owner' className='mt-0 flex-shrink-0' checked={selected} onChange={onSelect} />
    <span className='paragraph-medium medium zocks-text-gray-900'>{title}</span>
  </label>
)

interface UserSelectProps {
  label: string
  showLabel?: boolean
  users: User[]
  selectedUserId: string
  onChange: (userId: string) => void
}

const UserSelect: React.FC<UserSelectProps> = ({ label, showLabel = true, users, selectedUserId, onChange }) => {
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const options: SelectOption[] = users.map((user) => ({
    id: user.id,
    label: user.id === currentUser.id ? `${getUserDisplayName(user)} (you)` : getUserDisplayName(user),
  }))
  return (
    <div className='d-flex flex-column gap-1 flex-grow-1'>
      {showLabel ? <span className='paragraph-small medium zocks-text-gray-900'>{label}</span> : null}
      <SelectDropdown
        ariaLabel={label}
        options={options}
        initialSelectedOption={options.find((option) => option.id === selectedUserId) ?? null}
        placeholder={MEETING_OWNER_SELECT_USER}
        searchable
        hideClearButton
        onOptionSelected={(option) => onChange(option.id)}
      />
    </div>
  )
}

interface SkippedClient {
  id: string
  name: string
  owner?: { id: string; name: string }
}

interface NoOwnerBlockProps {
  withoutOwner: SkippedClient[]
  withUnsharedOwner: SkippedClient[]
  teammates: User[]
  fallback: FallbackMode
  onFallbackChange: (fallback: FallbackMode) => void
  backupOwnerId: string
  onBackupOwnerIdChange: (userId: string) => void
  onOpenInResults?: () => void
}

const SKIP_OPTION_ID = 'skip'

const NoOwnerBlock: React.FC<NoOwnerBlockProps> = ({
  withoutOwner,
  withUnsharedOwner,
  teammates,
  fallback,
  onFallbackChange,
  backupOwnerId,
  onBackupOwnerIdChange,
  onOpenInResults,
}) => {
  const noOwnerCount = withoutOwner.length
  const notSharedCount = withUnsharedOwner.length
  const totalCount = noOwnerCount + notSharedCount
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const me = teammates.find((teammate) => teammate.id === currentUser.id)
  const others = teammates.filter((teammate) => teammate.id !== currentUser.id).sort((a, b) => getUserDisplayName(a).localeCompare(getUserDisplayName(b)))
  const options: SelectOption[] = [
    { id: SKIP_OPTION_ID, label: noOwnerSkip(totalCount) },
    ...(me ? [{ id: me.id, label: sendFromOption(NO_OWNER_YOU) }] : []),
    ...others.map((teammate) => ({ id: teammate.id, label: sendFromOption(getUserDisplayName(teammate)) })),
  ]
  const selectedId = fallback === FallbackModes.SEND ? backupOwnerId : SKIP_OPTION_ID
  const backupOwner = teammates.find((teammate) => teammate.id === backupOwnerId)
  const backupOwnerName = backupOwnerId === currentUser.id ? NO_OWNER_YOU : backupOwner ? getUserDisplayName(backupOwner) : UNKNOWN_USER
  const groups: SkippedClientsGroup[] = [
    { key: 'noOwner', title: SKIPPED_GROUP_NO_OWNER, clients: withoutOwner },
    { key: 'ownerNotShared', title: SKIPPED_GROUP_OWNER_NOT_SHARED, clients: withUnsharedOwner.map((client) => ({ ...client, detail: client.owner?.name })) },
  ].filter((group) => group.clients.length > 0)

  const bothReasons = noOwnerCount > 0 && notSharedCount > 0
  const breakdown = bothReasons ? skippedBreakdownSentence(noOwnerCount, notSharedCount) : null
  const reason = (() => {
    if (bothReasons) {
      return `${cantSendFromOwnerSentence(totalCount)} ${breakdown}`
    }
    return noOwnerCount > 0 ? noOwnerSentence(noOwnerCount) : ownerNotSharedSentence(notSharedCount)
  })()
  const sentence =
    fallback === FallbackModes.SEND
      ? [sentFromInsteadSentence(totalCount, backupOwnerName), breakdown].filter(Boolean).join(' ')
      : `${reason} ${PICK_BACKUP_OWNER}`

  return (
    <div className='d-flex align-items-start gap-2 p-3 border rounded-3 zocks-border-color-gray-200 zocks-bg-color-gray-100'>
      <i className='bi bi-exclamation-circle paragraph-small zocks-text-gray-900' aria-hidden='true' />
      <div className='d-flex flex-column gap-2 flex-grow-1 mw-0'>
        <span className='paragraph-small bold zocks-text-gray-700'>
          {sentence} <SkippedClientsPopover groups={groups} onOpenInResults={onOpenInResults} />
        </span>
        <SelectDropdown
          ariaLabel={NO_OWNER_SELECT_LABEL}
          options={options}
          initialSelectedOption={options.find((option) => option.id === selectedId) ?? null}
          hideClearButton
          onOptionSelected={(option) => {
            if (option.id === SKIP_OPTION_ID) {
              onFallbackChange(FallbackModes.SKIP)
              return
            }
            onBackupOwnerIdChange(option.id)
            onFallbackChange(FallbackModes.SEND)
          }}
        />
      </div>
    </div>
  )
}

interface MeetingOwnerSectionProps {
  owners: SendBookingInvitationsOwners
  mode: MeetingOwnerMode
  onModeChange: (mode: MeetingOwnerMode) => void
  ownerUserId: string
  onOwnerUserIdChange: (userId: string) => void
  fallback: FallbackMode
  onFallbackChange: (fallback: FallbackMode) => void
  backupOwnerId: string
  onBackupOwnerIdChange: (userId: string) => void
  resultsLinkFor?: (clientIds: string[]) => (() => void) | undefined
}

export const MeetingOwnerSection: React.FC<MeetingOwnerSectionProps> = ({
  owners,
  mode,
  onModeChange,
  ownerUserId,
  onOwnerUserIdChange,
  fallback,
  onFallbackChange,
  backupOwnerId,
  onBackupOwnerIdChange,
  resultsLinkFor,
}) => {
  const clientsById = new Map(owners.clients.map((client) => [client.id, client]))
  const teammatesById = new Map(owners.teammates.map((teammate) => [teammate.id, teammate]))
  const toSkippedClients = (clientIds: string[], withOwner: boolean): SkippedClient[] =>
    clientIds.flatMap((clientId) => {
      const client = clientsById.get(clientId)
      if (!client) {
        return []
      }
      const ownerId = withOwner ? owners.ownerIdByClientId[clientId] : null
      const owner = ownerId ? teammatesById.get(ownerId) : undefined
      return [
        {
          id: clientId,
          name: getUserDisplayName(client),
          owner: ownerId ? { id: ownerId, name: owner ? getUserDisplayName(owner) : UNKNOWN_USER } : undefined,
        },
      ]
    })
  const skippedWithoutOwner = toSkippedClients(owners.clientIdsWithoutOwner, false)
  const skippedWithUnsharedOwner = toSkippedClients(owners.clientIdsWithUnsharedOwner, true)
  const sharedTeammates = owners.teammates.filter((teammate) => owners.sharedUserIds.has(teammate.id))

  const content = (() => {
    if (owners.isLoading) {
      return (
        <div className='d-flex justify-content-center py-3'>
          <Spinner />
        </div>
      )
    }
    if (owners.isError) {
      return <span className='paragraph-small zocks-text-gray-700'>{MEETING_OWNER_LOAD_ERROR}</span>
    }
    return (
      <>
        <div className='row g-2'>
          <div className='col-12 col-md-6'>
            <MeetingOwnerOption title={MEETING_OWNER_EACH} selected={mode === MeetingOwnerModes.EACH} onSelect={() => onModeChange(MeetingOwnerModes.EACH)} />
          </div>
          <div className='col-12 col-md-6'>
            <MeetingOwnerOption
              title={MEETING_OWNER_SINGLE}
              selected={mode === MeetingOwnerModes.SINGLE}
              onSelect={() => onModeChange(MeetingOwnerModes.SINGLE)}
            />
          </div>
        </div>
        {mode === MeetingOwnerModes.EACH ? (
          <>
            {skippedWithoutOwner.length > 0 || skippedWithUnsharedOwner.length > 0 ? (
              <NoOwnerBlock
                withoutOwner={skippedWithoutOwner}
                withUnsharedOwner={skippedWithUnsharedOwner}
                teammates={sharedTeammates}
                fallback={fallback}
                onFallbackChange={onFallbackChange}
                backupOwnerId={backupOwnerId}
                onBackupOwnerIdChange={onBackupOwnerIdChange}
                onOpenInResults={resultsLinkFor?.([...owners.clientIdsWithoutOwner, ...owners.clientIdsWithUnsharedOwner])}
              />
            ) : null}
          </>
        ) : (
          <UserSelect label={MEETING_OWNER_USER} showLabel={false} users={sharedTeammates} selectedUserId={ownerUserId} onChange={onOwnerUserIdChange} />
        )}
      </>
    )
  })()

  return (
    <FormSection title={MEETING_OWNER_TITLE} description={MEETING_OWNER_DESCRIPTION}>
      {content}
    </FormSection>
  )
}
